import { createFileRoute, useMatches, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import type { KeyboardEvent } from "react";
import { useEffect, useMemo, useState } from "react";
import { AlertCircle, Clock, LayoutGrid, List, Search, SlidersHorizontal } from "lucide-react";
import { AvatarImage, Avatar, AvatarFallback } from "@/components/ui/avatar";
import { TeamAvatars } from "@/components/common/team-avatars";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import type { CampaignAgentMode, CampaignResponse } from "@/types/campaign";
import { listCampaignsServer } from "@/server/campaigns";
import { getWorkspaceIdWithFallback } from "@/lib/routing/workspace-context";
import { ClientDateTime } from "@/components/common/client-date-time";
import { UpgradePlanModal } from "@/components/billing/UpgradePlanModal";
import type { WorkspaceLayoutLoaderData } from "@/routes/workspace/$workspaceSlug/route";
import type { CompanyInfoResponse } from "@/lib/api";
import type { TFunction } from "i18next";

export const Route = createFileRoute("/workspace/$workspaceSlug/campaigns/")({
  validateSearch: (search: Record<string, unknown>) => {
    const agentMode = search.agentMode;
    const validatedSearch: { agentMode?: CampaignAgentMode } = {};

    if (agentMode === "sourcing") {
      validatedSearch.agentMode = "sourcing";
    }

    return validatedSearch;
  },
  // Cache campaigns list for 10 seconds (moderately dynamic data)
  // Short cache since users may create/edit campaigns frequently
  staleTime: 10_000, // 10 seconds
  gcTime: 2 * 60_000, // 2 minutes until garbage collection
  loader: async ({ context, parentMatchPromise }) => {
    const workspaceId = await getWorkspaceIdWithFallback(context, "campaigns", parentMatchPromise);

    // Only call campaigns API - onboarding status already loaded by parent route
    const campaignsData = await listCampaignsServer({ data: { workspaceId } });

    const sortedCampaigns = (campaignsData.campaigns ?? []).slice().sort((a, b) => {
      const aTime = a.updated_at ? new Date(a.updated_at).getTime() : 0;
      const bTime = b.updated_at ? new Date(b.updated_at).getTime() : 0;
      return bTime - aTime;
    });

    return {
      campaigns: sortedCampaigns,
      total: campaignsData.total,
      workspaceId,
    };
  },
  component: CampaignsListPage,
});

function CampaignsListPage() {
  const { t } = useTranslation(["pages", "common"]);
  const navigate = useNavigate();
  const { workspaceSlug } = Route.useParams();
  const { agentMode } = Route.useSearch();
  const defaultAgentModeFilter: AgentModeFilter = agentMode === "sourcing" ? "sourcing" : "all";
  const { campaigns, workspaceId } = Route.useLoaderData();
  const matches = useMatches();
  const workspaceMatch = matches.find((match) => match.routeId === "/workspace/$workspaceSlug");
  const workspaceData = workspaceMatch?.loaderData as WorkspaceLayoutLoaderData | undefined;
  const billingSummary = workspaceData?.billingSummary;
  const currentPlan = billingSummary?.subscription?.plan_type?.toLowerCase() ?? "";
  const subscriptionStatus = billingSummary?.subscription?.status;
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("all");
  const [agentModeFilter, setAgentModeFilter] = useState<AgentModeFilter>(defaultAgentModeFilter);
  const [sortOption, setSortOption] = useState<SortOption>("updated_desc");
  const [layoutMode, setLayoutMode] = useState<CampaignLayoutMode>("card");
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);
  const hasActiveFilters =
    statusFilter !== "all" ||
    agentModeFilter !== defaultAgentModeFilter ||
    sortOption !== "updated_desc";

  useEffect(() => {
    setAgentModeFilter(defaultAgentModeFilter);
  }, [defaultAgentModeFilter]);

  const filteredCampaigns = useMemo(() => {
    const keyword = searchTerm.trim().toLowerCase();
    const result = campaigns.filter((campaign) => {
      if (campaign.status === "archived") {
        return false;
      }

      const byStatus = statusFilter === "all" || campaign.status === statusFilter;
      const byMode = agentModeFilter === "all" || campaign.agent_mode === agentModeFilter;

      if (!byStatus || !byMode) {
        return false;
      }

      if (!keyword) {
        return true;
      }

      const campaignName = campaign.name?.toLowerCase() ?? "";
      const companyNames = (campaign.companies ?? []).map((company) => company.name.toLowerCase());

      return campaignName.includes(keyword) || companyNames.some((name) => name.includes(keyword));
    });

    return result.slice().sort((a, b) => compareCampaigns(a, b, sortOption));
  }, [agentModeFilter, campaigns, searchTerm, sortOption, statusFilter]);
  const runningCampaignCount = campaigns.filter((campaign) => campaign.status === "active").length;
  const visibleCampaignCount = campaigns.filter(
    (campaign) => campaign.status !== "archived",
  ).length;
  const shouldShowStarterCampaignLimitBanner =
    currentPlan === "starter" &&
    (subscriptionStatus === "active" || subscriptionStatus === "trialing") &&
    runningCampaignCount >= 1;
  const emptyCampaignListLabel =
    visibleCampaignCount === 0
      ? t("pages:campaigns.listControls.emptyAll")
      : t("pages:campaigns.listControls.emptyResult");

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="flex w-full flex-1 flex-col">
        <div className="h-[64px] px-6">
          <div className="flex h-full min-w-0 items-center gap-6">
            <h1 className="shrink-0 text-[16px] font-semibold text-gray-900">
              {t("pages:campaigns.title")}
            </h1>
          </div>
        </div>
        <Separator className="bg-[#dfdfdf]" />

        <div className="flex-1 overflow-auto px-6 pt-4 pb-8">
          {shouldShowStarterCampaignLimitBanner ? (
            <div className="mb-4 flex min-h-11 items-center gap-3 rounded-[12px] border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
              <AlertCircle className="size-4 shrink-0 text-amber-700" />
              <p className="min-w-0 leading-5">
                <Trans
                  i18nKey="pages:campaigns.paywall.activeCampaignLimitBanner"
                  components={[
                    <button
                      key="upgrade-to-professional"
                      type="button"
                      className="font-semibold underline underline-offset-2 hover:text-amber-800"
                      onClick={() => setIsUpgradeModalOpen(true)}
                    />,
                  ]}
                />
              </p>
            </div>
          ) : null}
          <div className="mb-4 flex items-center justify-between gap-4">
            <div className="flex h-10 min-w-[260px] max-w-[680px] flex-1 items-center gap-3 rounded-full border border-transparent bg-muted/60 px-3 transition-colors focus-within:border-blue-500">
              <Search className="size-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1">
                <Input
                  value={searchTerm}
                  onChange={(event) => setSearchTerm(event.target.value)}
                  placeholder={t("pages:campaigns.listControls.searchPlaceholder")}
                  className="h-8 min-w-0 border-0 bg-transparent px-0 text-sm shadow-none placeholder:text-muted-foreground focus-visible:ring-0"
                />
              </div>
              <Tooltip>
                <DropdownMenu>
                  <TooltipTrigger asChild>
                    <DropdownMenuTrigger asChild>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="relative size-8 shrink-0 rounded-full bg-muted-foreground/10 text-foreground hover:bg-muted-foreground/15"
                        aria-label={t("pages:common.tooltips.showSearchOptions")}
                      >
                        <SlidersHorizontal className="size-4" />
                        {hasActiveFilters ? (
                          <span className="absolute top-1.5 right-1.5 size-2 rounded-full bg-blue-500" />
                        ) : null}
                      </Button>
                    </DropdownMenuTrigger>
                  </TooltipTrigger>
                  <DropdownMenuContent align="end" sideOffset={10} className="w-72 p-4">
                    <div className="space-y-2">
                      <DropdownMenuLabel className="px-0 text-xs font-semibold text-muted-foreground">
                        {t("pages:campaigns.listControls.status.placeholder")}
                      </DropdownMenuLabel>
                      <Select
                        value={statusFilter}
                        onValueChange={(value) => setStatusFilter(value as StatusFilter)}
                      >
                        <SelectTrigger className="h-10 w-full rounded-[12px] bg-background shadow-none">
                          <SelectValue
                            placeholder={t("pages:campaigns.listControls.status.placeholder")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">
                            {t("pages:campaigns.listControls.status.all")}
                          </SelectItem>
                          <SelectItem value="draft">{t("pages:campaigns.status.draft")}</SelectItem>
                          <SelectItem value="active">
                            {t("pages:campaigns.status.active")}
                          </SelectItem>
                          <SelectItem value="paused">
                            {t("pages:campaigns.status.paused")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-2">
                      <DropdownMenuLabel className="px-0 text-xs font-semibold text-muted-foreground">
                        {t("pages:campaigns.listControls.agentMode.placeholder")}
                      </DropdownMenuLabel>
                      <Select
                        value={agentModeFilter}
                        onValueChange={(value) => setAgentModeFilter(value as AgentModeFilter)}
                      >
                        <SelectTrigger className="h-10 w-full rounded-[12px] bg-background shadow-none">
                          <SelectValue
                            placeholder={t("pages:campaigns.listControls.agentMode.placeholder")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">
                            {t("pages:campaigns.listControls.agentMode.all")}
                          </SelectItem>
                          <SelectItem value="sales">
                            {t("pages:campaigns.agentMode.sales")}
                          </SelectItem>
                          <SelectItem value="sourcing">
                            {t("pages:campaigns.agentMode.sourcing")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <DropdownMenuSeparator className="-mx-4 my-3" />
                    <div className="space-y-2">
                      <DropdownMenuLabel className="px-0 text-xs font-semibold text-muted-foreground">
                        {t("pages:campaigns.listControls.sort.placeholder")}
                      </DropdownMenuLabel>
                      <Select
                        value={sortOption}
                        onValueChange={(value) => setSortOption(value as SortOption)}
                      >
                        <SelectTrigger className="h-10 w-full rounded-[12px] bg-background shadow-none">
                          <SelectValue
                            placeholder={t("pages:campaigns.listControls.sort.placeholder")}
                          />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="updated_desc">
                            {t("pages:campaigns.listControls.sort.updatedDesc")}
                          </SelectItem>
                          <SelectItem value="updated_asc">
                            {t("pages:campaigns.listControls.sort.updatedAsc")}
                          </SelectItem>
                          <SelectItem value="name_asc">
                            {t("pages:campaigns.listControls.sort.nameAsc")}
                          </SelectItem>
                          <SelectItem value="name_desc">
                            {t("pages:campaigns.listControls.sort.nameDesc")}
                          </SelectItem>
                          <SelectItem value="lead_count_desc">
                            {t("pages:campaigns.listControls.sort.leadCountDesc")}
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
                <TooltipContent side="bottom" align="end">
                  {t("pages:common.tooltips.showSearchOptions")}
                </TooltipContent>
              </Tooltip>
            </div>
            <div className="flex h-10 shrink-0 items-center rounded-full bg-muted/60 p-1">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  "size-8 rounded-full text-muted-foreground hover:bg-muted-foreground/10 hover:text-foreground",
                  layoutMode === "card" && "bg-white text-foreground shadow-sm hover:bg-white",
                )}
                aria-label={t("pages:campaigns.listControls.layout.card", "Card layout")}
                onClick={() => setLayoutMode("card")}
              >
                <LayoutGrid className="size-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className={cn(
                  "size-8 rounded-full text-muted-foreground hover:bg-muted-foreground/10 hover:text-foreground",
                  layoutMode === "list" && "bg-white text-foreground shadow-sm hover:bg-white",
                )}
                aria-label={t("pages:campaigns.listControls.layout.list", "List layout")}
                onClick={() => setLayoutMode("list")}
              >
                <List className="size-4" />
              </Button>
            </div>
          </div>

          {filteredCampaigns.length === 0 ? (
            <div className="flex h-[220px] items-center justify-center rounded-2xl border border-dashed border-border/70 bg-muted/20">
              <p className="text-sm text-muted-foreground">{emptyCampaignListLabel}</p>
            </div>
          ) : layoutMode === "card" ? (
            <div
              data-testid="campaign-card-grid"
              className="grid grid-cols-[repeat(auto-fill,minmax(320px,512px))] justify-start gap-6"
            >
              {filteredCampaigns.map((campaign) => {
                return (
                  <CampaignCard
                    key={campaign.id}
                    campaign={campaign}
                    onSelect={() =>
                      navigate({
                        to: "/workspace/$workspaceSlug/campaigns/$campaignId",
                        params: {
                          workspaceSlug,
                          campaignId: campaign.id,
                        },
                      })
                    }
                  />
                );
              })}
            </div>
          ) : (
            <CampaignListTable
              campaigns={filteredCampaigns}
              onSelect={(campaignId) =>
                navigate({
                  to: "/workspace/$workspaceSlug/campaigns/$campaignId",
                  params: {
                    workspaceSlug,
                    campaignId,
                  },
                })
              }
            />
          )}
        </div>
      </div>
      <UpgradePlanModal
        open={isUpgradeModalOpen}
        onOpenChange={setIsUpgradeModalOpen}
        workspaceId={workspaceId}
        workspaceSlug={workspaceSlug}
        currentPlan={currentPlan}
        defaultSelectedPlan="professional"
        noticeTitle={t("pages:campaigns.paywall.limitTitle")}
        noticeDescription={t("pages:campaigns.paywall.limitDescription", {
          plan: "Starter",
          limit: 1,
        })}
      />
    </div>
  );
}

const AVATAR_GRADIENTS = [
  "from-sky-500 to-blue-600",
  "from-amber-500 to-orange-600",
  "from-emerald-500 to-teal-600",
  "from-violet-500 to-purple-600",
  "from-rose-500 to-pink-600",
  "from-indigo-500 to-blue-700",
  "from-teal-500 to-emerald-600",
];

// STATUS_LABELS key/value moved to i18n pages:campaigns.status

const STATUS_STYLES: Record<string, string> = {
  draft: "bg-[#FFF4CC] border-[#FFECB2] text-[#705500]",
  active: "bg-[#F3FBF7] border-[#D7EDE3] text-[#1B6243]",
  paused: "bg-[#F6F7F8] border-[#EEEFF1] text-[#7E7E7E]",
  archived: "bg-gray-100 text-gray-600",
};

const AGENT_MODE_STYLES: Record<CampaignAgentMode, string> = {
  sales: "bg-[#EEF6FF] border-[#D9E9FF] text-[#1E4F8A]",
  sourcing: "bg-[#ECFDF3] border-[#CFF7DF] text-[#166534]",
  website: "bg-[#F5F0FF] border-[#E5D9FF] text-[#5B21B6]",
};

type StatusFilter = "all" | CampaignResponse["status"];
type AgentModeFilter = "all" | CampaignAgentMode;
type SortOption = "updated_desc" | "updated_asc" | "name_asc" | "name_desc" | "lead_count_desc";
type CampaignLayoutMode = "card" | "list";

function getCompanyAvatars(companies: CompanyInfoResponse[]) {
  return (
    companies.map((companyItem) => ({
      name: companyItem.name,
      src: companyItem.logo_url || "",
      fallback: companyItem.name.slice(0, 1),
    })) || []
  );
}

function CampaignListTable({
  campaigns,
  onSelect,
}: {
  campaigns: CampaignResponse[];
  onSelect: (campaignId: string) => void;
}) {
  const { t } = useTranslation(["pages", "common"]);

  return (
    <div className="overflow-hidden rounded-[16px] border border-border/60 bg-card">
      <Table className="table-fixed">
        <TableHeader>
          <TableRow className="border-b border-[#dfdfdf] bg-muted/30 hover:bg-muted/30">
            <TableHead className="w-[34%] px-4">
              {t("pages:campaigns.listControls.table.name", "Name")}
            </TableHead>
            <TableHead className="w-[16%] px-4">
              {t("pages:campaigns.listControls.table.agent", "Agent")}
            </TableHead>
            <TableHead className="w-[14%] px-4">
              {t("pages:campaigns.listControls.table.status", "Status")}
            </TableHead>
            <TableHead className="w-[16%] px-4">
              {t("pages:campaigns.listControls.table.companies", "Companies")}
            </TableHead>
            <TableHead className="w-[20%] px-4">
              {t("pages:campaigns.listControls.table.updated", "Updated")}
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {campaigns.map((campaign) => {
            const statusConfig = getStatusConfig(campaign.status, t);
            const agentModeConfig = getAgentModeConfig(campaign.agent_mode, t);
            const companyMembers = getCompanyAvatars(campaign.companies ?? []);
            const displayedCompanyMembers = companyMembers.slice(0, 5);
            const companyCount = campaign.countLeads ?? companyMembers.length;
            const extraCount = Math.max(companyCount - displayedCompanyMembers.length, 0);

            return (
              <TableRow
                key={campaign.id}
                className="h-[68px] cursor-pointer border-b border-gray-100 last:border-b-0"
                onClick={() => onSelect(campaign.id)}
              >
                <TableCell className="max-w-0 px-4">
                  <div className="flex min-w-0 items-center gap-3">
                    <Avatar className="size-9 shrink-0">
                      {campaign.logo && (
                        <AvatarImage src={campaign.logo} alt={campaign.name ?? ""} />
                      )}
                      <AvatarFallback
                        className={cn(
                          "bg-gradient-to-br text-sm font-semibold uppercase tracking-tight text-white",
                          pickAvatarGradient(campaign.name ?? t("pages:campaigns.newCampaign")),
                        )}
                      >
                        {getInitial(campaign.name ?? t("pages:campaigns.newCampaign"))}
                      </AvatarFallback>
                    </Avatar>
                    <span className="min-w-0 truncate font-medium text-foreground">
                      {campaign.name ?? t("pages:campaigns.newCampaign")}
                    </span>
                  </div>
                </TableCell>
                <TableCell className="px-4">
                  <Badge
                    className={cn(
                      "px-3 py-1 text-xs font-semibold capitalize transition-colors",
                      agentModeConfig.className,
                    )}
                  >
                    {agentModeConfig.label}
                  </Badge>
                </TableCell>
                <TableCell className="px-4">
                  <Badge
                    className={cn(
                      "px-3 py-1 text-xs font-semibold capitalize transition-colors",
                      statusConfig.className,
                    )}
                  >
                    {statusConfig.label}
                  </Badge>
                </TableCell>
                <TableCell className="px-4">
                  {companyCount > 0 ? (
                    <TeamAvatars members={displayedCompanyMembers} extraCount={extraCount} />
                  ) : (
                    <span className="text-sm text-muted-foreground">-</span>
                  )}
                </TableCell>
                <TableCell className="px-4 text-sm text-muted-foreground">
                  <ClientDateTime dateTime={campaign.updated_at} />
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function CampaignCard({
  campaign,
  onSelect,
}: {
  campaign: CampaignResponse;
  onSelect: () => void;
}) {
  const { t } = useTranslation(["pages", "common"]);
  const avatarInitial = getInitial(campaign.name ?? t("pages:campaigns.newCampaign"));
  const statusConfig = getStatusConfig(campaign.status, t);
  const agentModeConfig = getAgentModeConfig(campaign.agent_mode, t);
  const companyMembers = getCompanyAvatars(campaign.companies ?? []);
  const displayedCompanyMembers = companyMembers.slice(0, 5);
  const companyCount = campaign.countLeads ?? companyMembers.length;
  const extraCount = Math.max(companyCount - displayedCompanyMembers.length, 0);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onSelect();
    }
  };

  return (
    <div
      role="button"
      tabIndex={0}
      data-testid="campaign-card"
      className="group flex h-[168px] w-full cursor-pointer flex-col justify-between gap-2 rounded-[16px] border border-border/60 bg-card px-4 py-4 transition-all hover:bg-card/80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 focus-visible:ring-offset-2 focus-visible:ring-offset-card"
      onClick={onSelect}
      onKeyDown={handleKeyDown}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-4">
          <Avatar className="size-14">
            {campaign.logo && <AvatarImage src={campaign.logo} alt={campaign.name ?? ""} />}
            <AvatarFallback
              className={cn(
                "bg-gradient-to-br text-base font-semibold uppercase tracking-tight text-white",
                pickAvatarGradient(campaign.name ?? t("pages:campaigns.newCampaign")),
              )}
            >
              {avatarInitial}
            </AvatarFallback>
          </Avatar>
          <div className="min-w-0 flex-1 space-y-1">
            <h3 className="line-clamp-2 max-h-[2.8125rem] max-w-[220px] overflow-hidden text-lg font-semibold leading-tight text-foreground">
              {campaign.name ?? t("pages:campaigns.newCampaign")}
            </h3>
          </div>
        </div>
        <div className="flex flex-wrap justify-end gap-2">
          <Badge
            className={cn(
              "px-3 py-1 text-xs font-semibold capitalize transition-colors",
              agentModeConfig.className,
            )}
          >
            {agentModeConfig.label}
          </Badge>
        </div>
      </div>
      {companyCount > 0 && (
        <div className="flex h-9 w-full items-center justify-end gap-3">
          <TeamAvatars members={displayedCompanyMembers} extraCount={extraCount} />
        </div>
      )}

      <div className="flex items-center gap-4 text-sm text-muted-foreground">
        <Badge
          className={cn(
            "px-3 py-1 text-xs font-semibold capitalize transition-colors",
            statusConfig.className,
          )}
        >
          {statusConfig.label}
        </Badge>
        <div className="ml-auto flex items-center gap-2 whitespace-nowrap bg-[#F4F5F6] px-4 py-2 rounded-[16px]">
          <Clock className="size-4 text-muted-foreground" />
          <ClientDateTime dateTime={campaign.updated_at} />
        </div>
      </div>
    </div>
  );
}

function pickAvatarGradient(seed: string) {
  const index = Math.abs(hashString(seed)) % AVATAR_GRADIENTS.length;
  return AVATAR_GRADIENTS[index];
}

function hashString(value: string) {
  return value.split("").reduce((acc, char) => acc + char.charCodeAt(0), 0);
}

function getInitial(value: string) {
  return value?.trim().charAt(0)?.toUpperCase() || "?";
}

function getStatusConfig(
  status: CampaignResponse["status"],
  t: TFunction<readonly ["pages", "common"], undefined>,
) {
  const label = t(`pages:campaigns.status.${status}`) ?? toTitleCase(status);
  const className = STATUS_STYLES[status] ?? "bg-gray-100 text-gray-600";

  return {
    label,
    className,
  };
}

function getAgentModeConfig(
  agentMode: CampaignResponse["agent_mode"] | undefined,
  t: TFunction<readonly ["pages", "common"], undefined>,
) {
  const mode = agentMode ?? "sales";
  const label = t(`pages:campaigns.agentMode.${mode}`) ?? toTitleCase(mode);
  const className = AGENT_MODE_STYLES[mode] ?? AGENT_MODE_STYLES.sales;

  return {
    label,
    className,
  };
}

function toTitleCase(value: string) {
  return value.replace(/[_-]+/g, " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function compareCampaigns(a: CampaignResponse, b: CampaignResponse, sortOption: SortOption) {
  const updatedA = a.updated_at ? new Date(a.updated_at).getTime() : 0;
  const updatedB = b.updated_at ? new Date(b.updated_at).getTime() : 0;
  const nameA = (a.name ?? "").toLowerCase();
  const nameB = (b.name ?? "").toLowerCase();
  const countA = a.countLeads ?? 0;
  const countB = b.countLeads ?? 0;

  switch (sortOption) {
    case "updated_asc":
      return updatedA - updatedB;
    case "name_asc":
      return nameA.localeCompare(nameB);
    case "name_desc":
      return nameB.localeCompare(nameA);
    case "lead_count_desc":
      return countB - countA;
    case "updated_desc":
    default:
      return updatedB - updatedA;
  }
}
